import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {AbstractCrudService} from '../shared/abstract-crud.service';
import {Linking} from './linking.model';
import { AuthService } from '~app/auth/auth.service';
import { VivoService } from '~app/vivo/vivo.service';
import { CacheService } from '~app/shared/cache.service';
import { MappingService } from '~app/linking/mapping.service';

/**
 * Service for interacting with Linkings.
 */
@Injectable()
export class LinkingService extends AbstractCrudService<Linking> {
  /**
   * URL for current model, override url from superclass
   * @type {string}
   */
  protected url = environment.regoApiURL + 'linkings';

  private linkings: Promise<Linking[]>;

  /** How long dynamically resolved VIVO IDs are cached for, in milliseconds */
  private readonly VIVO_CACHE_TIME: Duration = { days: 1 };

  /**
   * Default constructor
   * @param {HttpClient} http - refined http service, automatically adds authentication token
   */
  constructor(
    protected http: HttpClient,
    private authService: AuthService,
    private vivoService: VivoService,
    private cacheService: CacheService,
  ) {
    super(http);
  }

  protected async getLinkings(): Promise<Linking[]> {
    if (!this.linkings) {
      this.linkings = this.list().then(result => {
        if (result.data) {
          return result.data;
        }
        console.warn('No linkings found');
        return [];
      }).catch(err => {
        console.error('Error loading linkings', err);
        return [];
      });
    }
    return this.linkings;
  }

  private dynamicLinking(regoId: string, sourceId: string, sourceSystem: string): Linking {
    return {
      id: 0,
      createdAt: new Date(),
      deletedAt: new Date(),
      updatedAt: new Date(),
      regoId,
      sourceId,
      sourceSystem,
    };
  }

  /**
   * find source system identifier that is linked to rego identifier.
   *
   * order of finding the id:
   * 1) check from manual linking table
   * 2) check according specific sourcesystem
   * 2.1) vivo check directly from vivo api
   * 2.2) misonet "deviceid" from rego geojson (layer's unique feature id)
   *
   * @param regoId
   * @param sourceSystem
   */
  public async getLinkingsForRegoId(regoId: string, sourceSystem?: string): Promise<Linking[]> {
    //// check if manual linking
    const linkings = (await this.getLinkings())
      .filter(o => `${o.regoId}` === `${regoId}` && (sourceSystem === undefined || `${o.sourceSystem}` === `${sourceSystem}`));
    if (linkings.length>0) {
      return linkings;
    }

    // resolve from VIVO API if possible
    if (sourceSystem === 'vivo') {
      const propertyId = this.authService.getTenantConfig()?.vivo?.lookupPropertyId;
      if (propertyId) {
        const sourceIds = await this.cacheService.getOrSet<string[], string[]>(
          ['LinkingService VIVO property to ID', propertyId, regoId],
          this.VIVO_CACHE_TIME,
          async () => {
            const response = await this.vivoService.rawSearch({
              page: 1,
              limit: 100,
              filters: [
                {
                  property: { _id: propertyId, type: 'text' },
                  option: { value: 'equals' },
                  query: [
                    { key: 'fields.field', value: propertyId },
                    { key: 'fields.value', value: regoId },
                  ],
                  value: regoId,
                }
              ],
              viewOptions: {
                orderby: 'updatedAt',
                reverse: true,
                dontPopulateFields: true,
                hideSubAssets: true,
              }
            });
            return response?.docs && response.docs.map(doc => doc._id);
          }
        );
        if (sourceIds) {
          return sourceIds.map(sourceId => this.dynamicLinking(regoId, sourceId, sourceSystem));
        }
      }
    }
    //// as a final effort, try to match against rego geojson files (uniqueBy)
    return [this.dynamicLinking(regoId, regoId, sourceSystem)];
  }

  /**
   * find rego identifier that is linked to some other systems id.
   *
   * @param sourceId
   * @param sourceSystem
   * @returns
   */
  public async getLinkingsForSourceId(sourceId: string | number, sourceSystem?: string): Promise<Linking[]> {
    //// first check if manual linking is don
    const linkings = (await this.getLinkings())
      .filter(o => `${o.sourceId}` === `${sourceId}` && (sourceSystem === undefined || `${o.sourceSystem}` === `${sourceSystem}`));
    if (linkings.length>0) {
      return linkings;
    }

    // resolve from VIVO API if possible
    if (sourceSystem === 'vivo') {
      const propertyId = this.authService.getTenantConfig()?.vivo?.lookupPropertyId;
      if (propertyId) {
        const regoIds = await this.cacheService.getOrSet<string[], string[]>(
          ['LinkingService ID to VIVO property', propertyId, sourceId.toString()],
          this.VIVO_CACHE_TIME,
          async () => {
            const result = await this.vivoService.getAssetInfo(sourceId.toString());
            return result?._id && [result._id];
          }
        );
        if (regoIds) {
          return regoIds.map(regoId => this.dynamicLinking(regoId, sourceId.toString(), sourceSystem));
        }
      }
    }

    //// as a final effort, try to match against rego geojson files (uniqueBy)
    return [this.dynamicLinking(''+sourceId, ''+sourceId, sourceSystem)];
  }
}
